import React, { useEffect, useRef, useState } from "react";
import {
  GoogleMap,
  Polyline,
  OverlayView,
  useJsApiLoader,
} from "@react-google-maps/api";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import polyline from "@mapbox/polyline";
import { IMapViewProps } from "./types";
import Marker from "./Marker";
import { mapStyles } from "./MapStyles";

const startMarkerStyle: React.CSSProperties = {
  position: "absolute",
  left: "-20px",
  top: "-48px",
  width: "40px",
  height: "48px",
  pointerEvents: "none",
};

const commonMarkerStyle: React.CSSProperties = {
  position: "absolute",
  left: "-18px",
  top: "-36px",
  width: "36px",
  height: "36px",
  pointerEvents: "none",
};

const StartMarker: React.FC<{ lat: number; lng: number }> = () => (
  <Marker style={startMarkerStyle}>
    <img
      src="/map-pickup-icon.png"
      alt="Pickup"
      width={40}
      height={45}
      style={{
        display: "block",
        width: "40px",
        height: "45px",
        pointerEvents: "none",
      }}
    />
  </Marker>
);

const EndMarker: React.FC<{ lat: number; lng: number }> = () => (
  <Marker style={commonMarkerStyle}>
    <img
      src="/map-dropoff-icon.png"
      alt="Pickup"
      width={40}
      height={45}
      style={{
        display: "block",
        width: "40px",
        height: "45px",
        pointerEvents: "none",
      }}
    />
  </Marker>
);

const TaxiMarker: React.FC<{ lat: number; lng: number; angle?: number }> = ({
  angle = 0,
}) => (
  <Marker
    style={{
      position: "absolute",
      left: "-18px",
      top: "-18px",
      width: "36px",
      height: "36px",
      pointerEvents: "none",
      transform: `rotate(${angle}deg)`,
      transformOrigin: "center",
      transition: "transform 0.2s linear",
    }}
  >
    <img
      src="/taxi_icon2.png"
      alt="Taxi"
      width={10}
      height={10}
      style={{
        display: "block",
        width: "30px",
        height: "30px",
        objectFit: "contain",
        pointerEvents: "none",
      }}
    />
  </Marker>
);

const getAngle = (
  from: { lat: number; lng: number },
  to: { lat: number; lng: number },
) => {
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const toDeg = (rad: number) => (rad * 180) / Math.PI;

  const lat1 = toRad(from.lat);
  const lat2 = toRad(to.lat);
  const dLng = toRad(to.lng - from.lng);

  const y = Math.sin(dLng) * Math.cos(lat2);
  const x =
    Math.cos(lat1) * Math.sin(lat2) -
    Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLng);

  let brng = toDeg(Math.atan2(y, x));
  return (brng + 360) % 360;
};

const getSegmentBearing = (
  path: { lat: number; lng: number }[],
  point: { lat: number; lng: number },
  minSegmentLength = 0.00009,
) => {
  if (path.length < 2) return 0;
  let minDist = Infinity;
  let segIdx = 0;
  for (let i = 0; i < path.length - 1; i++) {
    const segLen = Math.hypot(
      //@ts-ignore
      path[i + 1].lat - path[i].lat,
      //@ts-ignore
      path[i + 1].lng - path[i].lng,
    );
    if (segLen < minSegmentLength) continue;
    //@ts-ignore
    const dist = Math.hypot(point.lat - path[i].lat, point.lng - path[i].lng);
    if (dist < minDist) {
      minDist = dist;
      segIdx = i;
    }
  }
  const from = path[segIdx];
  const to = path[segIdx + 1];
  //@ts-ignore
  return getAngle(from, to);
};

const MapView: React.FC<IMapViewProps> = ({
  start,
  end,
  zoom = 14,
  apiKey,
  encodedPolyline,
  status,
  driverHeartbeat,
  driver,
}) => {
  const [mapCenter, setMapCenter] = useState<{
    lat: number;
    lng: number;
  } | null>(null);
  const userInteracted = useRef(false);

  let path: { lat: number; lng: number }[] = [];
  let polyStart = start;
  let polyEnd = end;

  if (status === "enroute" && driver?.shape && driverHeartbeat && start) {
    const decoded = polyline.decode(driver.shape ?? "");
    path = decoded.map(([lat, lng]) => ({ lat: lat * 0.1, lng: lng * 0.1 }));
    if (
      path.length === 0 ||
      //@ts-ignore
      path[0].lat !== driverHeartbeat.lat ||
      //@ts-ignore
      path[0].lng !== driverHeartbeat.lng
    ) {
      path.unshift(driverHeartbeat);
    }
    if (
      path.length === 0 ||
      //@ts-ignore
      path[path.length - 1].lat !== start.lat ||
      //@ts-ignore
      path[path.length - 1].lng !== start.lng
    ) {
      path.push(start);
    }

    polyStart = driverHeartbeat;
    polyEnd = start;
  } else if (
    status === "intransit" &&
    driver?.shape &&
    driverHeartbeat &&
    end
  ) {
    const decoded = polyline.decode(driver.shape ?? "");
    path = decoded.map(([lat, lng]) => ({ lat: lat * 0.1, lng: lng * 0.1 }));
    if (
      path.length === 0 ||
      //@ts-ignore
      path[0].lat !== driverHeartbeat.lat ||
      //@ts-ignore
      path[0].lng !== driverHeartbeat.lng
    ) {
      path.unshift(driverHeartbeat);
    }
    if (
      path.length === 0 ||
      //@ts-ignore
      path[path.length - 1].lat !== end.lat ||
      //@ts-ignore
      path[path.length - 1].lng !== end.lng
    ) {
      path.push(end);
    }

    polyStart = driverHeartbeat;
    polyEnd = end;
  } else if (encodedPolyline) {
    const decoded = polyline.decode(encodedPolyline ?? "");
    path = decoded.map(([lat, lng]) => ({ lat, lng }));

    if (decoded.length > 0) {
      //@ts-ignore
      polyStart = { lat: decoded[0][0], lng: decoded[0][1] };
      polyEnd = {
        //@ts-ignore
        lat: decoded[decoded.length - 1][0],
        //@ts-ignore
        lng: decoded[decoded.length - 1][1],
      };
    }
  } else if (start && end) {
    path = [start, end];
  }

  useEffect(() => {
    if (!userInteracted.current && polyStart) {
      setMapCenter(polyStart);
    }
  }, [polyStart?.lat, polyStart?.lng]);

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: apiKey,
  });

  let taxiMarkerPosition: { lat: number; lng: number } | null =
    driverHeartbeat ?? null;

  let targetPoint: { lat: number; lng: number } | null = null;
  if (status === "enroute" && start) {
    targetPoint = start;
  } else if (status === "intransit" && end) {
    targetPoint = end;
  }

  let taxiAngle = 0;
  if (
    taxiMarkerPosition &&
    (status === "enroute" || status === "intransit") &&
    path.length > 1 &&
    path[0] !== undefined &&
    path[1] !== undefined
  ) {
    taxiAngle = getSegmentBearing(path, taxiMarkerPosition);
    // taxiAngle = getAngle(path[0], path[1]);
  } else if (taxiMarkerPosition && targetPoint) {
    taxiAngle = getAngle(taxiMarkerPosition, targetPoint);
  }

  if (loadError) return <div>Map cannot be loaded right now.</div>;
  if (!isLoaded || !start || !end) return <div>Map loading...</div>;

  return (
    <div className="h-full w-full rounded-lg overflow-hidden relative">
      <GoogleMap
        mapContainerClassName="h-full w-full rounded-lg overflow-hidden relative"
        center={mapCenter || polyStart}
        zoom={zoom}
        onDragStart={() => (userInteracted.current = true)}
        onZoomChanged={() => (userInteracted.current = true)}
        options={{
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
          styles: mapStyles,
        }}
      >
        {path.length > 0 && (
          <Polyline
            path={path}
            options={{ strokeColor: "#2563eb", strokeWeight: 4 }}
          />
        )}
        <OverlayView
          position={start}
          mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
        >
          <StartMarker lat={start.lat} lng={start.lng} />
        </OverlayView>
        <OverlayView
          position={end}
          mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
        >
          <EndMarker lat={end.lat} lng={end.lng} />
        </OverlayView>
        {taxiMarkerPosition && (
          <OverlayView
            position={taxiMarkerPosition}
            mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
          >
            <TaxiMarker
              lat={taxiMarkerPosition.lat}
              lng={taxiMarkerPosition.lng}
              angle={taxiAngle}
            />
          </OverlayView>
        )}
      </GoogleMap>
    </div>
  );
};

export default MapView;
